import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  AddIssueItem,
  CreateIssue,
  IssueItemsPage,
  IssueSource,
  ReportIssue,
  ScheduleIssue,
  SendIssue,
  TranslateRequest,
  TranslationLanguage,
  UpdateIssue,
} from '@headline/schemas'
import { api } from './api'
import { fail, throwIfLateError } from './query-fail'

export * from './derek-queries'

/**
 * Report issues (CLI-695) — the editor's queries and mutations. The writes to an issue's rows,
 * which go to the server one at a time, are issue-item-writes.ts.
 *
 * Keys nest under `['issue', issueId]` so one invalidation after any edit refreshes the shell
 * (header counts), the visible item list, the shelf and the preview together. Item lists are keyed
 * per (header, source) — the editor loads exactly what is on screen, never the whole issue — and
 * carry a short `gcTime`, because a Paramount-sized report visited header by header would
 * otherwise leave every page it passed through in the heap for the default five minutes.
 */

const ITEMS_GC_TIME = 2 * 60 * 1000

export function issuesQuery(reportId: string) {
  return queryOptions({
    queryKey: ['report', reportId, 'issues'],
    queryFn: async () => {
      const res = await api.api['report-issues'].$get({ query: { reportId } })
      if (!res.ok) return fail(res, 'Failed to load previous reports')
      return res.json()
    },
  })
}

export function issueQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].$get({ param: { id: issueId } })
      if (res.status === 404)
        throw new Error('This report no longer exists — it may have been deleted.')
      if (!res.ok) return fail(res, 'Failed to load the report')
      return res.json()
    },
  })
}

/**
 * Every row of a list the server pages, as one page with nothing more to fetch. A header is shown
 * whole: reading only the first page hid every article past the 200th — still kept, so still in
 * the preview and the send — and removing the visible ones never brought the rest into view
 * (CLI-1031). Removed rows count toward a page, so the loop follows `hasMore`, never `items`.
 */
async function readAllItemPages(
  readPage: (offset: number) => Promise<IssueItemsPage>,
): Promise<IssueItemsPage> {
  const byId = new Map<string, IssueItemsPage['items'][number]>()
  let offset = 0
  let hasMore = true
  while (hasMore) {
    const page = await readPage(offset)
    for (const item of page.items) byId.set(item.id, item)
    offset += page.items.length
    // An empty page that claims more would loop forever; take it as the end.
    hasMore = page.hasMore && page.items.length > 0
  }
  return { items: [...byId.values()], hasMore: false }
}

export function issueItemsQuery(issueId: string, sectionId: string, source: IssueSource) {
  return queryOptions({
    queryKey: ['issue', issueId, 'items', sectionId, source],
    queryFn: () =>
      readAllItemPages(async (offset) => {
        const res = await api.api['report-issues'][':id'].items.$get({
          param: { id: issueId },
          query: { sectionId, source, offset: String(offset) },
        })
        if (!res.ok) return fail(res, 'Failed to load this header')
        return res.json()
      }),
    gcTime: ITEMS_GC_TIME,
  })
}

export function issueShelfQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'shelf'],
    queryFn: () =>
      readAllItemPages(async (offset) => {
        const res = await api.api['report-issues'][':id'].items.$get({
          param: { id: issueId },
          query: { shelf: '1', offset: String(offset) },
        })
        if (!res.ok) return fail(res, 'Failed to load the shelf')
        return res.json()
      }),
    gcTime: ITEMS_GC_TIME,
  })
}

/** Producer snippets and the licensed full text — fetched on expand, one card at a time. */
export function issueItemDetailQuery(issueId: string, itemId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'item-detail', itemId],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].items[':itemId'].detail.$get({
        param: { id: issueId, itemId },
      })
      if (!res.ok) return fail(res, 'Failed to load the article')
      return res.json()
    },
    // Full text is large and nobody re-reads it; let it go as soon as the card collapses.
    gcTime: 30 * 1000,
  })
}

/**
 * The rendered email. Never served stale: preview is the send gate, so it re-renders on every
 * open, and the HTML string is dropped from the heap a minute after the pane closes.
 */
export function issuePreviewQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'preview'],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].preview.$get({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to render the preview')
      return res.json()
    },
    staleTime: 0,
    gcTime: 60 * 1000,
  })
}

/**
 * What the report would send right now (CLI-625): the editor's pull and the mailer's render for an
 * issue the API builds in memory and never writes — nothing is created, so there is no draft to
 * open afterwards. Mounted
 * by the dialog that shows it, so mounting is the request; never cached across opens (each is a
 * fresh pull, and that is the point), and dropped from the heap soon after the dialog closes.
 */
export function reportPreviewQuery(reportId: string) {
  return queryOptions({
    queryKey: ['report', reportId, 'preview'],
    queryFn: async () => {
      const res = await api.api.reports[':id'].preview.$get({ param: { id: reportId } })
      if (!res.ok) return fail(res, 'Failed to build the preview')
      return throwIfLateError(await res.json(), 'Failed to build the preview')
    },
    staleTime: 0,
    gcTime: 60 * 1000,
    // A preview is a real pull — minutes for the largest reports — and a retry would run it again.
    retry: false,
  })
}

/**
 * Staff's "Write as Google Doc" (CLI-925) and "Write as Google Sheet" (CLI-940): the same build as
 * the preview, also written as the file a send would deliver. A mutation because it creates a real
 * file; the response is the preview, with the file's link.
 */
export function useReportDeliverablePreview(kind: 'googleDoc' | 'googleSheet') {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const door = api.api.reports[':id'].preview
      const res =
        kind === 'googleDoc'
          ? await door['google-doc'].$post({ param: { id: reportId } })
          : await door['google-sheet'].$post({ param: { id: reportId } })
      const failed = `Failed to write the ${DELIVERABLE_NAMES[kind]}`
      if (!res.ok) return fail(res, failed)
      return throwIfLateError(await res.json(), failed)
    },
  })
}

export const DELIVERABLE_NAMES = { googleDoc: 'Google Doc', googleSheet: 'Google Sheet' } as const

export function issueSendsQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'sends'],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].sends.$get({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to load sends')
      return res.json()
    },
  })
}

/**
 * Search is debounced by the caller and the term rides in the key, so each settled term is one
 * cache entry — with `gcTime: 0`, gone the moment the results are no longer on screen.
 */
/** Find in report: the draft's own articles mentioning a term. Volatile key, so it is not kept. */
export function findInIssueQuery(issueId: string, term: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'find', term],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].find.$get({
        param: { id: issueId },
        query: { q: term },
      })
      if (!res.ok) return fail(res, 'Search failed')
      return res.json()
    },
    gcTime: 0,
    staleTime: 10 * 1000,
  })
}

export function searchHitsQuery(issueId: string, term: string, source: IssueSource | undefined) {
  return queryOptions({
    queryKey: ['issue', issueId, 'search', source ?? 'all', term],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id']['search-hits'].$get({
        param: { id: issueId },
        query: { q: term, ...(source ? { source } : {}) },
      })
      if (!res.ok) return fail(res, 'Search failed')
      return res.json()
    },
    gcTime: 0,
    staleTime: 30 * 1000,
  })
}

// ── Mutations ──────────────────────────────────────────────────────────────────────────────────

/**
 * The delivery queue (`reportsOverviewQuery` in queries.ts) is built from today's issues, so any
 * mutation that changes an issue's standing today — created, scheduled, cancelled, sent — has to
 * refetch it, or the queue keeps showing the row it just acted on until someone reloads. Spelled
 * as the key rather than imported, to keep this module's imports one-directional.
 */
const refetchTodaysQueue = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: ['reports-overview'] })

export function useCreateIssue(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: Omit<CreateIssue, 'reportId'>) => {
      const res = await api.api['report-issues'].$post({ json: { reportId, ...input } })
      if (!res.ok) return fail(res, 'Failed to create the draft')
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['report', reportId, 'issues'] })
      void refetchTodaysQueue(queryClient)
    },
  })
}

export function useDeleteIssue(reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (issueId: string) => {
      const res = await api.api['report-issues'][':id'].$delete({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to delete the draft')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report', reportId, 'issues'] }),
  })
}

export function usePullIssue(issueId: string, queryClient: QueryClient) {
  return useMutation({
    /** `byDay`: pull just this issue header again, one day at a time (CLI-994). */
    mutationFn: async (byDay?: string) => {
      const res = await api.api['report-issues'][':id'].pull.$post({
        param: { id: issueId },
        query: byDay ? { byDay } : {},
      })
      if (!res.ok) return fail(res, 'Pull failed')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issue', issueId] }),
  })
}

export function useAddIssueItem(issueId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: AddIssueItem) => {
      const res = await api.api['report-issues'][':id'].items.$post({
        param: { id: issueId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to add the article')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issue', issueId] }),
  })
}

export function useSendIssue(issueId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: SendIssue) => {
      const res = await api.api['report-issues'][':id'].send.$post({
        param: { id: issueId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Send failed')
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueId] })
      void refetchTodaysQueue(queryClient)
    },
  })
}

/** A new coverage window. Items already pulled stay; the next pull reads the new bounds. */
export function useUpdateIssueWindow(issueId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: UpdateIssue) => {
      const res = await api.api['report-issues'][':id'].$patch({
        param: { id: issueId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to change the coverage window')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issue', issueId] }),
  })
}

/**
 * The reviewed mark on a header, shown in the rail the instant it is clicked: the shell is
 * rewritten optimistically and reconciled after.
 */
export function useSetSectionReviewed(issueId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { sectionId: string; reviewed: boolean }) => {
      const res = await api.api['report-issues'][':id'].sections[':sectionId'].$patch({
        param: { id: issueId, sectionId: input.sectionId },
        json: { reviewed: input.reviewed },
      })
      if (!res.ok) return fail(res, 'Failed to mark the header')
      return res.json()
    },
    onMutate: async ({ sectionId, reviewed }) => {
      const key = issueQuery(issueId).queryKey
      await queryClient.cancelQueries({ queryKey: key })
      queryClient.setQueryData<ReportIssue>(
        key,
        (issue) =>
          issue && {
            ...issue,
            sections: issue.sections.map((section) =>
              section.id === sectionId
                ? { ...section, reviewedAt: reviewed ? new Date().toISOString() : null }
                : section,
            ),
          },
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['issue', issueId] }),
  })
}

/**
 * What a translation would cost, priced off the cache without sending anything. Enabled only while
 * the modal is open and a scope is chosen — the point is to answer before anyone commits.
 *
 * The request body is in the key because the price depends on every part of it: change the
 * language or tick another field and it is a different question.
 */
export function translatePreflightQuery(issueId: string, ask: TranslateRequest) {
  return queryOptions({
    queryKey: ['issue', issueId, 'translate-preflight', ask],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].translate.preflight.$post({
        param: { id: issueId },
        json: ask,
      })
      if (!res.ok) return fail(res, 'Failed to price the translation')
      return res.json()
    },
    // A price, not a document: cheap to ask again and wrong to keep once an edit has landed.
    gcTime: 30 * 1000,
  })
}

export function useTranslate(issueId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (ask: TranslateRequest) => {
      const res = await api.api['report-issues'][':id'].translate.$post({
        param: { id: issueId },
        json: ask,
      })
      if (!res.ok) return fail(res, 'Failed to translate')
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['issue', issueId] }),
  })
}

/**
 * One article's full text, translated for reading. Never stored server-side — see the route — so
 * this cache IS the only thing standing between re-opening a card and paying again. Hence the
 * long `gcTime` and `staleTime`: within a sitting, re-expanding a card costs nothing.
 */
export function fullTextTranslationQuery(
  issueId: string,
  itemId: string,
  targetLang: TranslationLanguage,
) {
  return queryOptions({
    queryKey: ['issue', issueId, 'full-text-translation', itemId, targetLang],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].items[':itemId'][
        'full-text-translation'
      ].$post({ param: { id: issueId, itemId }, json: { targetLang } })
      if (!res.ok) return fail(res, 'Failed to translate the full text')
      return res.json()
    },
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  })
}

export function useScheduleIssue(issueId: string, reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: ScheduleIssue) => {
      const res = await api.api['report-issues'][':id'].schedule.$post({
        param: { id: issueId },
        json: input,
      })
      if (!res.ok) return fail(res, 'Failed to schedule the send')
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueId] })
      void queryClient.invalidateQueries({ queryKey: ['report', reportId, 'issues'] })
      void refetchTodaysQueue(queryClient)
    },
  })
}

export function useCancelSchedule(issueId: string, reportId: string, queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api['report-issues'][':id'].schedule.$delete({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to cancel the schedule')
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueId] })
      void queryClient.invalidateQueries({ queryKey: ['report', reportId, 'issues'] })
      void refetchTodaysQueue(queryClient)
    },
  })
}

/** The edit trail, newest first. Nested under the issue key so every edit refreshes it. */
export function issueEventsQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'events'],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].events.$get({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to load the edit history')
      return res.json()
    },
    gcTime: ITEMS_GC_TIME,
  })
}
